import React, { Component } from "react";
import { Container } from "react-bootstrap";
import Switch from '@mui/material/Switch';
import axios from "axios";
import Swal from "sweetalert2";
import PageHeader from '../shared/PageHeader';
import { CARD } from '../shared/txStyles';
import { hasAction } from '../../../helper';

/* Setup → System Settings (2026-11-04, T1.74, user's decision): admin on/off switches.

   Two stock switches, both for ITEMS only (Sale Invoice, Warehouse Item Sale, Warehouse Item
   Refund); device sales are not affected:
     - stock movement: whether those saves, edits, deletes and refunds change stock quantities;
     - stock check:    whether a sale / refund that would take stock below zero is refused.

   The server applies them (helper/StockSwitch.php); this screen only reads and sets them. Changing
   one needs EDIT on this screen in Role Rights — the server re-checks that. */

const SCREEN = '/admin/system-settings';

const SETTINGS = [
    {
        key: 'stock_movement',
        title: 'Stock movement',
        on: 'Item sales, edits, deletes and refunds add to and take from stock.',
        off: 'Item sales and refunds are saved without changing stock. Delivery charges still apply.',
    },
    {
        key: 'stock_check',
        title: 'Stock check',
        on: 'A sale or refund that would take stock below zero is refused.',
        off: 'Sales go through even when stock is short; stock can go negative.',
    },
];

const ROW = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '16px 0', borderBottom: '1px solid #e5e7eb' };
const NAME = { fontWeight: 600, fontSize: 15, color: '#111827' };
const TEXT = { fontSize: 13.5, color: '#4b5563', marginTop: 4, maxWidth: 560 };
const META = { fontSize: 12, color: '#6b7280', marginTop: 6 };

class SystemSettings extends Component {
    constructor(props) {
        super(props);
        this.state = { values: {}, meta: {}, loaded: false, saving: '' };
    }

    componentDidMount() {
        this.load();
    }

    async load() {
        this.props.toggleLoader && this.props.toggleLoader(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Setups/AppSetting.php`, {});
            if (res.data.success === 1) {
                const values = {}, meta = {};
                (res.data.data || []).forEach(r => {
                    values[r.SETTING_KEY] = String(r.SETTING_VALUE) === '1';
                    meta[r.SETTING_KEY] = r;
                });
                this.setState({ values, meta, loaded: true });
            } else {
                Swal.fire('Error', res.data.message || 'The settings could not be loaded.', 'error');
            }
        } catch (e) {
            Swal.fire('Error', 'The settings could not be loaded.', 'error');
        } finally {
            this.props.toggleLoader && this.props.toggleLoader(false);
        }
    }

    async change(setting, turnOn) {
        const ok = await Swal.fire({
            title: `Turn ${setting.title.toLowerCase()} ${turnOn ? 'on' : 'off'}?`,
            text: turnOn ? setting.on : setting.off,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: turnOn ? 'Turn on' : 'Turn off',
            cancelButtonText: 'Cancel',
        });
        if (!ok.isConfirmed) return;

        this.setState({ saving: setting.key });
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/mutation/Setups/AppSetting.php`, {
                SETTING_KEY: setting.key,
                SETTING_VALUE: turnOn ? '1' : '0',
            });
            if (res.data.success === 1) {
                this.props.toggleSnackBar && this.props.toggleSnackBar(true, 'Saved', `${setting.title} is ${turnOn ? 'on' : 'off'}.`, 'success');
                await this.load();
            } else {
                Swal.fire('Not saved', res.data.message || 'The setting could not be saved.', 'error');
            }
        } catch (e) {
            Swal.fire('Not saved', 'The setting could not be saved.', 'error');
        } finally {
            this.setState({ saving: '' });
        }
    }

    render() {
        const { values, meta, loaded, saving } = this.state;
        const canEdit = hasAction(SCREEN, 'EDIT');
        return (
            <Container>
                <PageHeader title="System Settings" />
                <div style={{ ...CARD, padding: '4px 20px 8px' }}>
                    {SETTINGS.map(s => {
                        const on = !!values[s.key];
                        const m = meta[s.key] || {};
                        return (
                            <div key={s.key} style={ROW}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={NAME}>{s.title}: {loaded ? (on ? 'On' : 'Off') : '…'}</div>
                                    <div style={TEXT}>{on ? s.on : s.off}</div>
                                    {m.UPDATED_AT && (
                                        <div style={META}>Last changed {m.UPDATED_AT}{m.UPDATED_BY ? ` by ${m.UPDATED_BY}` : ''}</div>
                                    )}
                                </div>
                                <Switch
                                    id={`setting-${s.key}`}
                                    checked={on}
                                    disabled={!loaded || !canEdit || saving !== ''}
                                    onChange={(e) => this.change(s, e.target.checked)}
                                    inputProps={{ 'aria-label': s.title }}
                                />
                            </div>
                        );
                    })}
                    <div style={{ ...META, padding: '12px 0 4px' }}>
                        Items only: Sale Invoice, Warehouse Item Sale and Warehouse Item Refund. Device sales are not affected.
                        {!canEdit && ' You can view these settings; changing them needs the Edit right on this screen.'}
                    </div>
                </div>
            </Container>
        );
    }
}

export default SystemSettings;
